import { action, computed, makeObservable, observable } from 'mobx'

import type { Iter, ObjKey } from '@/types'
import { getValueFromKeyIter } from '@/utils/common'

// TODO: Map을 Extended 하게 만들기
export class ExtendedMap<K, V> {
  protected source: Map<K, V>

  constructor(iterableIterator?: IterableIterator<[K, V]> | Map<K, V>) {
    makeObservable<ExtendedMap<K, V>, 'source'>(this, {
      source: observable,
      size: computed,
      isEmpty: computed,
      isNotEmpty: computed,
      toArr: computed,
      delete: action,
      deletes: action,
      adds: action,
      clear: action,
      set: action,
      addFromArr: action,
    })

    if (iterableIterator === undefined) {
      this.source = new Map()
    } else if (iterableIterator instanceof Map) {
      this.source = iterableIterator
    } else {
      this.source = new Map(iterableIterator)
    }
  }

  get [Symbol.toStringTag]() {
    return 'Extended Map'
  }

  get size() {
    return this.source.size
  }

  get isEmpty() {
    return this.source.size === 0
  }

  get isNotEmpty() {
    return this.source.size > 0
  }

  get toArr() {
    return [...this.values()]
  }

  static fromArr<keyT extends keyof valT, valT extends { [key in keyT]: valT[keyT] }>(
    coll: valT[],
    key: Iter<keyT, valT>,
  ) {
    const map = new this(new Map(coll.map((value) => [getValueFromKeyIter(value, key), value])))
    return map
  }

  static fromObj<objT extends { [key in ObjKey]: valT }, valT>(obj: objT) {
    const map = new Map<keyof objT, objT[keyof objT]>()
    for (const key in obj) {
      map.set(key, obj[key])
    }
    return new this(map)
  }

  static fromIter<K extends ObjKey, V>(iterableIterator: IterableIterator<[K, V]>) {
    return new this(iterableIterator)
  }

  keys() {
    return this.source.keys()
  }

  values() {
    return this.source.values()
  }

  entries() {
    return this.source.entries()
  }

  [Symbol.iterator]() {
    return this.source.entries()
  }

  set(key: K, value: V) {
    this.source.set(key, value)
    return this
  }

  get(key: K) {
    return this.source.get(key)
  }

  has(key: K) {
    return this.source.has(key)
  }

  delete(key: K) {
    this.source.delete(key)
    return this
  }

  deletes(keys: K[]) {
    keys.map((key) => this.delete(key))
    return this
  }

  clear() {
    this.source.clear()
    return this
  }

  forEach: ExtendedMap<K, V>['source']['forEach'] = (...args) => {
    this.source.forEach(...args)
  }

  adds(entries: IterableIterator<[K, V]>) {
    for (const [key, value] of entries) {
      this.set(key, value)
    }
  }

  addFromArr<K extends keyof V, V>(arr: V[], key: K) {
    for (let i = 0; i < arr.length; i++) {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      this.source.set(arr[i][key], arr[i])
    }
  }
}

export default ExtendedMap
